import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { colors } from "../../../../styles/colors";

export interface GewichtBalkenSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

const GEWICHT_MIN_ANTEIL = 2;

/**
 * 100%-gestapelter Balken zur Visualisierung einer Score-Gewichtung.
 * Die Grenzen zwischen den Segmenten lassen sich per Drag verschieben –
 * das verschiebt Gewicht nur zwischen den beiden angrenzenden Segmenten,
 * die Summe aller Gewichte bleibt dabei unverändert.
 */
const GewichtBalken: React.FC<{
  segments: GewichtBalkenSegment[];
  onChange: (updates: Record<string, number>) => void;
}> = ({ segments, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const summe =
    segments.reduce((sum, s) => sum + Math.max(s.value, 0), 0) || 100;
  const rawCumulative = segments.reduce<number[]>((acc, s, i) => {
    const prev = i === 0 ? 0 : acc[i - 1];
    acc.push(prev + Math.max(s.value, 0));
    return acc;
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (draggingIndex === null || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const rawBoundary = (relativeX / rect.width) * summe;

      const leftStart =
        draggingIndex === 0 ? 0 : rawCumulative[draggingIndex - 1];
      const rightEnd = rawCumulative[draggingIndex + 1];
      const minAnteil = (GEWICHT_MIN_ANTEIL / 100) * summe;
      const newBoundary = Math.min(
        Math.max(rawBoundary, leftStart + minAnteil),
        rightEnd - minAnteil,
      );

      const left = segments[draggingIndex];
      const right = segments[draggingIndex + 1];

      onChange({
        [left.key]: Math.round(newBoundary - leftStart),
        [right.key]: Math.round(rightEnd - newBoundary),
      });
    },
    [draggingIndex, rawCumulative, segments, summe, onChange],
  );

  useEffect(() => {
    if (draggingIndex === null) return undefined;
    const handlePointerUp = () => setDraggingIndex(null);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [draggingIndex, handlePointerMove]);

  return (
    <Box>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          width: "100%",
          height: 48,
          borderRadius: "24px",
          overflow: "hidden",
          userSelect: draggingIndex !== null ? "none" : "auto",
        }}
      >
        {segments.map((segment, index) => {
          const anteil = Math.max(segment.value, 0) / summe;
          if (anteil <= 0) return null;
          return (
            <Box
              key={segment.key}
              sx={{
                position: "relative",
                flex: anteil,
                backgroundColor: segment.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                marginRight: index < segments.length - 1 ? "2px" : 0,
                transition:
                  draggingIndex === null ? "flex 0.2s ease-in-out" : "none",
              }}
            >
              <Typography
                sx={{
                  color: colors.chart.text,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  px: 1,
                  pointerEvents: "none",
                }}
              >
                {segment.label} {Math.round(segment.value)} %
              </Typography>
              {index < segments.length - 1 && (
                <Box
                  onPointerDown={(event) => {
                    event.preventDefault();
                    setDraggingIndex(index);
                  }}
                  sx={{
                    position: "absolute",
                    right: -8,
                    top: 0,
                    bottom: 0,
                    width: 16,
                    cursor: "ew-resize",
                    zIndex: 2,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GewichtBalken;
